import { h, mount } from "@/lib/dom";
import { icon, type IconName } from "@/lib/icons";
import { navigate } from "@/app/router";
import { topicsService, notesService, subjectsService, materialsService, pbErrorMessage } from "@/services";
import type { NoteDocument, Topic, Subject, Material } from "@/types/models";

/**
 * Globale Suche: Volltextsuche über Fächer, Themen, Notizinhalte und
 * Materialnamen. Suche läuft lokal (alle Daten bereits in memory/PB cached).
 */
export function SearchPage(): HTMLElement {
  const input = h("input", {
    class: "search-input",
    type: "search",
    placeholder: "Fach, Thema, Material oder Stichwort suchen …",
    autofocus: "true",
  }) as HTMLInputElement;

  const resultsEl = h("div", { class: "search-results" });
  const container = h("div", { class: "search-page" },
    h("div", { class: "page-header" },
      h("h1", null, "Suche"),
    ),
    h("div", { class: "search-bar" },
      h("span", { class: "search-icon", html: icon("search", 18) }),
      input
    ),
    resultsEl
  );

  let debounce: number | undefined;
  let allTopics: Topic[] = [];
  let allNotes: NoteDocument[] = [];
  let allSubjects: Subject[] = [];
  let allMaterials: Material[] = [];
  let loaded = false;

  async function ensureLoaded() {
    if (loaded) return;
    try {
      [allSubjects, allTopics] = await Promise.all([
        subjectsService.list().catch(() => [] as Subject[]),
        topicsService.listAll(),
      ]);
      const [nestedNotes, nestedMats] = await Promise.all([
        Promise.all(allTopics.map((t) => notesService.listByTopic(t.id).catch(() => [] as NoteDocument[]))),
        Promise.all(allTopics.map((t) => materialsService.listByTopic(t.id).catch(() => [] as Material[]))),
      ]);
      allNotes = nestedNotes.flat();
      allMaterials = nestedMats.flat();
      loaded = true;
    } catch (err) {
      mount(resultsEl, h("div", { class: "card card-pad field-error" }, pbErrorMessage(err)));
    }
  }

  input.addEventListener("input", () => {
    clearTimeout(debounce);
    debounce = window.setTimeout(async () => {
      const q = input.value.trim().toLowerCase();
      if (!q || q.length < 2) { resultsEl.innerHTML = ""; return; }
      await ensureLoaded();
      const results = search(q);
      renderResults(results, q);
    }, 200);
  });

  // Sofort laden damit erste Suche schnell ist
  void ensureLoaded();

  function search(q: string): SearchResult[] {
    const results: SearchResult[] = [];

    // Fächer (höchste Priorität – grobe Navigation)
    for (const subject of allSubjects) {
      if (subject.title.toLowerCase().includes(q)) {
        results.push({ type: "subject", subject, score: 120, excerpt: subject.category ?? "" });
      }
    }

    for (const topic of allTopics) {
      if (topic.state === "archived") continue;
      const titleMatch = topic.title.toLowerCase().includes(q);
      if (titleMatch) {
        results.push({ type: "topic", topic, score: 100, excerpt: "" });
      }
    }

    // Materialien (Dateiname/Titel)
    for (const m of allMaterials) {
      const topic = allTopics.find((t) => t.id === m.topic);
      if (!topic || topic.state === "archived") continue;
      if (m.title.toLowerCase().includes(q)) {
        results.push({ type: "material", topic, material: m, score: 70, excerpt: m.type ?? "" });
      }
    }

    for (const note of allNotes) {
      const topic = allTopics.find((t) => t.id === note.topic);
      if (!topic || topic.state === "archived") continue;
      const content = note.content ?? "";
      const lc = content.toLowerCase();
      const pos = lc.indexOf(q);
      if (pos === -1) continue;
      // Kontext-Snippet um den Treffer
      const start = Math.max(0, pos - 60);
      const end = Math.min(content.length, pos + q.length + 80);
      let excerpt = (start > 0 ? "…" : "") + content.slice(start, end).trim() + (end < content.length ? "…" : "");
      // Query highlighten
      excerpt = excerpt.replace(new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"),
        (m) => `<mark>${m}</mark>`);
      results.push({ type: "note", topic, note, score: 50, excerpt });
    }

    // Themen-Treffer zuerst, dann Notizen
    return results.sort((a, b) => b.score - a.score).slice(0, 30);
  }

  function renderResults(results: SearchResult[], q: string) {
    if (!results.length) {
      mount(resultsEl, h("div", { class: "card card-pad muted", style: "margin-top:12px" },
        `Keine Ergebnisse für „${q}".`));
      return;
    }
    mount(resultsEl, h("div", { class: "col", style: "margin-top:12px; gap:8px" },
      ...results.map((r) => resultCard(r))
    ));
  }

  function resultCard(r: SearchResult): HTMLElement {
    let kindLabel: string;
    let kindIcon: IconName;
    let title: string;
    let onClick: () => void;

    switch (r.type) {
      case "subject":
        kindLabel = "Fach"; kindIcon = "folder"; title = r.subject!.title;
        onClick = () => navigate("/");
        break;
      case "material":
        kindLabel = "Material"; kindIcon = "file"; title = r.material!.title;
        onClick = () => navigate(`/topic/${r.topic!.id}`);
        break;
      case "topic":
        kindLabel = "Thema"; kindIcon = "book"; title = r.topic!.title;
        onClick = () => navigate(`/topic/${r.topic!.id}`);
        break;
      default:
        kindLabel = r.note?.kind === "mitschrift" ? "Mitschrift" : "Lernzettel";
        kindIcon = "file-text"; title = r.topic!.title;
        onClick = () => navigate(`/topic/${r.topic!.id}/edit/${r.note!.kind}`);
    }

    // Untertitel: bei Material/Note das zugehörige Thema zeigen
    const sub = (r.type === "material" || r.type === "note") ? r.topic!.title : "";
    return h("button", { class: "search-result-card", type: "button", onclick: onClick },
      h("div", { class: "sr-kind", html: icon(kindIcon, 15) }, ` ${kindLabel}`),
      h("div", { class: "sr-title" }, title),
      sub ? h("div", { class: "muted text-sm" }, sub) : null,
      r.excerpt
        ? h("div", { class: "sr-excerpt", html: r.excerpt })
        : null
    );
  }

  return container;
}

interface SearchResult {
  type: "subject" | "topic" | "material" | "note";
  topic?: Topic;
  subject?: Subject;
  material?: Material;
  note?: NoteDocument;
  score: number;
  excerpt: string;
}
